import * as THREE from 'three';
import { MMLElementType } from './types';
import { ASSET_TYPE } from '@src/components/scene/react-three-components/sceneAsset';

export function convertThreeJSToMML(selectedModel: any): string {
  if (!selectedModel) return '';

  const threeObj = selectedModel.props?.asset?.ref?.current;
  const assetType = selectedModel.props?.asset?.type;

  const position = threeObj.position;
  const rotation = threeObj.rotation;
  const scale = threeObj.scale;

  let modelUrl = selectedModel.props.asset.url;

  const name = threeObj.name || '';
  const objectType = determineObjectType(threeObj, assetType);

  if (assetType === ASSET_TYPE.MML_OBJECT && modelUrl.startsWith('<m-')) {
    const mmlTagMatch = modelUrl.match(/<(m-[a-z]+)/);
    if (mmlTagMatch) {
      const tagType = mmlTagMatch[1];

      const commonAttributes = `
  id="${name.split('-')[0] || 'object'}" 
  x="${position.x.toFixed(2)}" 
  y="${position.y.toFixed(2)}" 
  z="${position.z.toFixed(2)}"
  rx="${THREE.MathUtils.radToDeg(rotation.x).toFixed(2)}" 
  ry="${THREE.MathUtils.radToDeg(rotation.y).toFixed(2)}" 
  rz="${THREE.MathUtils.radToDeg(rotation.z).toFixed(2)}"
  sx="${scale.x.toFixed(2)}" 
  sy="${scale.y.toFixed(2)}" 
  sz="${scale.z.toFixed(2)}"`;

      const attributesMatch = modelUrl.match(
        /src="([^"]+)"|color="([^"]+)"|width="([^"]+)"|height="([^"]+)"|content="([^"]+)"|loop="([^"]+)"|start-time="([^"]+)"|intensity="([^"]+)"|type="([^"]+)"/g
      );
      let additionalAttributes = '';

      if (attributesMatch) {
        additionalAttributes = ' ' + attributesMatch.join(' ');
      }

      return `<${tagType}${commonAttributes}${additionalAttributes}></${tagType}>`;
    }

    return modelUrl;
  }

  const commonAttributes = `
  id="${name.split('-')[0] || 'object'}" 
  x="${position.x.toFixed(2)}" 
  y="${position.y.toFixed(2)}" 
  z="${position.z.toFixed(2)}"
  rx="${THREE.MathUtils.radToDeg(rotation.x).toFixed(2)}" 
  ry="${THREE.MathUtils.radToDeg(rotation.y).toFixed(2)}" 
  rz="${THREE.MathUtils.radToDeg(rotation.z).toFixed(2)}"
  sx="${scale.x.toFixed(2)}" 
  sy="${scale.y.toFixed(2)}" 
  sz="${scale.z.toFixed(2)}"`;

  switch (objectType) {
    case MMLElementType.CUBE:
      let cubeColor = 'red';
      if (
        threeObj instanceof THREE.Mesh &&
        threeObj.material instanceof THREE.MeshStandardMaterial
      ) {
        cubeColor = '#' + threeObj.material.color.getHexString();
      }
      return `<m-cube${commonAttributes} color="${cubeColor}"></m-cube>`;

    case MMLElementType.SPHERE:
      let sphereColor = 'green';
      if (
        threeObj instanceof THREE.Mesh &&
        threeObj.material instanceof THREE.MeshStandardMaterial
      ) {
        sphereColor = '#' + threeObj.material.color.getHexString();
      }
      return `<m-sphere${commonAttributes} color="${sphereColor}"></m-sphere>`;

    case MMLElementType.CYLINDER:
      let cylinderColor = 'blue';
      if (
        threeObj instanceof THREE.Mesh &&
        threeObj.material instanceof THREE.MeshStandardMaterial
      ) {
        cylinderColor = '#' + threeObj.material.color.getHexString();
      }
      return `<m-cylinder${commonAttributes} color="${cylinderColor}"></m-cylinder>`;

    case MMLElementType.PLANE:
      let planeColor = 'white';
      if (
        threeObj instanceof THREE.Mesh &&
        threeObj.material instanceof THREE.MeshStandardMaterial
      ) {
        planeColor = '#' + threeObj.material.color.getHexString();
      }
      return `<m-plane${commonAttributes} color="${planeColor}" width="5" height="3"></m-plane>`;

    case MMLElementType.LIGHT:
      let lightType = 'spotlight';
      let intensity = '1';
      if (threeObj instanceof THREE.SpotLight) {
        lightType = 'spotlight';
        intensity = threeObj.intensity.toString();
      } else if (threeObj instanceof THREE.DirectionalLight) {
        lightType = 'directional';
        intensity = threeObj.intensity.toString();
      } else if (threeObj instanceof THREE.PointLight) {
        lightType = 'point';
        intensity = threeObj.intensity.toString();
      }
      return `<m-light${commonAttributes} type="${lightType}" intensity="${intensity}"></m-light>`;

    case MMLElementType.AUDIO:
      return `<m-audio${commonAttributes} src="${modelUrl}" start-time="0" loop="true"></m-audio>`;

    case MMLElementType.IMAGE:
      let imageWidth = '1';
      let imageHeight = '1';
      if (
        threeObj instanceof THREE.Mesh &&
        threeObj.geometry instanceof THREE.PlaneGeometry
      ) {
        imageWidth = threeObj.geometry.parameters.width.toString();
        imageHeight = threeObj.geometry.parameters.height.toString();
      }
      return `<m-image${commonAttributes} src="${modelUrl}" width="${imageWidth}" height="${imageHeight}"></m-image>`;

    case MMLElementType.VIDEO:
      let videoWidth = '1';
      let videoHeight = '1';
      if (
        threeObj instanceof THREE.Mesh &&
        threeObj.geometry instanceof THREE.PlaneGeometry
      ) {
        videoWidth = threeObj.geometry.parameters.width.toString();
        videoHeight = threeObj.geometry.parameters.height.toString();
      }
      return `<m-video${commonAttributes} src="${modelUrl}" width="${videoWidth}" height="${videoHeight}" start-time="0" loop="true"></m-video>`;

    case MMLElementType.LABEL:
      return `<m-label${commonAttributes} content="Hello World!" font-size="80" alignment="center" width="10"></m-label>`;

    case MMLElementType.GROUP:
      return `<m-group${commonAttributes}></m-group>`;

    case MMLElementType.MODEL:
    default:
      return `<m-model${commonAttributes} src="${modelUrl}"></m-model>`;
  }
}

function determineObjectType(
  object: THREE.Object3D,
  assetType?: string
): MMLElementType {
  if (assetType) {
    if (Object.values(ASSET_TYPE.GLTF).includes(assetType)) {
      return MMLElementType.MODEL;
    }

    if (assetType === ASSET_TYPE.PRIMITIVE.BOX) {
      return MMLElementType.CUBE;
    }
    if (assetType === ASSET_TYPE.PRIMITIVE.SPHERE) {
      return MMLElementType.SPHERE;
    }
    if (assetType === ASSET_TYPE.MISC.POSITIONAL_AUDIO) {
      return MMLElementType.AUDIO;
    }
    if (assetType === ASSET_TYPE.MISC.LIGHT) {
      return MMLElementType.LIGHT;
    }
    if (assetType === ASSET_TYPE.PRIMITIVE_TEXTURE.IMAGE_PLANE) {
      return MMLElementType.IMAGE;
    }
    if (assetType === ASSET_TYPE.PRIMITIVE_TEXTURE.VIDEO_PLANE) {
      return MMLElementType.VIDEO;
    }
    return MMLElementType.MODEL;
  }

  if (object instanceof THREE.Mesh) {
    if (object.geometry instanceof THREE.BoxGeometry) {
      return MMLElementType.CUBE;
    } else if (object.geometry instanceof THREE.SphereGeometry) {
      return MMLElementType.SPHERE;
    } else if (object.geometry instanceof THREE.CylinderGeometry) {
      return MMLElementType.CYLINDER;
    } else if (object.geometry instanceof THREE.PlaneGeometry) {
      return MMLElementType.PLANE;
    }
  }

  if (object instanceof THREE.Light) {
    return MMLElementType.LIGHT;
  }

  return MMLElementType.MODEL;
}
